import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BLACK, TEXT_MUTED } from '../../components/ProposalFlow/theme';
import { usePlanCatalog } from './usePlanCatalog';
import { PlanComparisonTable } from './PlanComparisonTable';
import { ProductionSection } from './ProductionSection';
import { UsageRatesSection } from './UsageRatesSection';

// Internal plan comparison page (/pricing, internal roles only since
// 2026-09-16 — it was a public iframe embed before). Keeps the bare-shell
// look from src/pages/publicProposal/ProposalPage.tsx: ?bg= background
// override, ?heading=0 hides the title, ?plan=<key> highlights one column.
// Plans/features come from the API's plan catalog (admin > Pricing > Plans),
// so copy edits need no deploy. Three columns: Free / Standard Brand /
// Enterprise (the enterprise plans share one column — see the API's
// PLAN_FEATURE_ROWS comment). TDC Production renders beneath as its own
// add-on block, never as plan columns.

const PAGE_BG = '#FAFAF8';
const FONT = "'DM Sans', sans-serif";

// Heading/footnote colors follow the page background so an embed on a
// dark site (?bg=1C1A17) stays readable; the table itself keeps its own
// card background.
function isDarkHex(hex: string): boolean {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5;
}

function parseBgParam(raw: string | null): string {
  if (!raw) return PAGE_BG;
  const cleaned = raw.replace(/^#/, '').trim();
  if (/^[0-9a-fA-F]{3}$/.test(cleaned) || /^[0-9a-fA-F]{6}$/.test(cleaned)) {
    return `#${cleaned}`;
  }
  return PAGE_BG;
}

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const bgColor = useMemo(() => parseBgParam(searchParams.get('bg')), [searchParams]);
  const highlightPlan = searchParams.get('plan');
  const hideHeading = searchParams.get('heading') === '0';
  const { data, loading, error } = usePlanCatalog();
  const onDark = useMemo(() => isDarkHex(bgColor), [bgColor]);
  const headingColor = onDark ? '#FFFFFF' : BLACK;
  const mutedColor = onDark ? 'rgba(255,255,255,0.72)' : TEXT_MUTED;

  useEffect(() => {
    document.body.classList.add('pdp-audit-embed');
    return () => {
      document.body.classList.remove('pdp-audit-embed');
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: bgColor, fontFamily: FONT }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');`}</style>
      <div style={{ width: 1100, maxWidth: '100%', margin: '0 auto', padding: '48px 20px' }}>
        {!hideHeading && (
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h1 style={{ fontSize: 32, fontWeight: 700, color: headingColor, margin: 0 }}>
              Commerce Content OS Plans &amp; Pricing
            </h1>
            <p style={{ fontSize: 15, color: mutedColor, marginTop: 10 }}>
              If you have a product page and video content, or you need video content, we can help.
              Run Commerce Content Ops on our Commerce Content OS — Product &amp; PDP analysis, scores,
              catalog intelligence, and campaign strategies… everything measured and improved.
            </p>
          </div>
        )}
        {loading && (
          <div style={{ textAlign: 'center', color: mutedColor, padding: 40 }}>Loading plans…</div>
        )}
        {!loading && error && (
          <div style={{ textAlign: 'center', color: mutedColor, padding: 40 }}>{error}</div>
        )}
        {!loading && !error && data && (
          <PlanComparisonTable
            plans={data.plans}
            featureRows={data.featureRows}
            collapsedGroups={data.collapsedGroups}
            highlightPlan={highlightPlan}
          />
        )}
        <p style={{ fontSize: 12, color: mutedColor, textAlign: 'center', marginTop: 18 }}>
          Analyses per month = products on Autopilot × cadence. Enterprise allowances, analysis cadence and
          overage rates are set per contract.
        </p>
        {!loading && !error && data && <UsageRatesSection headingColor={headingColor} mutedColor={mutedColor} />}
        {!loading && !error && data && data.production && (
          <ProductionSection
            offering={data.production}
            headingColor={headingColor}
            mutedColor={mutedColor}
          />
        )}
      </div>
    </div>
  );
}
